import * as React from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { Button, SlideOver } from '@/components/ui';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';

// TICKET-EXPORT-01: full multi-section export drawer, matching the
// Freshdesk-style reference screenshot (format radio, ticket/contact/
// company field checklists with a "select all" toggle per section).

export interface ExportFieldDef {
  key: string;
  label: string;
}

interface FieldCatalog {
  ticketFields: ExportFieldDef[];
  contactFields: ExportFieldDef[];
  companyFields: ExportFieldDef[];
}

interface ExportTicketsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Current list filters (status/priority/groupId/etc) applied as-is, mirroring the visible ticket list. */
  filters: Record<string, string | number>;
  /** When set, exports exactly these ticket ids instead of the current filters (multi-select export). */
  ticketIds?: string[];
}

function FieldSection({
  title,
  fields,
  selected,
  onToggle,
  onToggleAll,
}: {
  title: string;
  fields: ExportFieldDef[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: () => void;
}) {
  const { t } = useI18n();
  const allSelected = fields.length > 0 && fields.every((field) => selected.has(field.key));
  return (
    <div className="border-t pt-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <span className="text-xs text-ink-subtle">{t(selected.size === 1 ? 'export.fieldsSelectedOne' : 'export.fieldsSelectedMany', { count: selected.size })}</span>
      </div>
      <label className="mb-2 flex items-center gap-2 text-sm text-brand-700">
        <input type="checkbox" checked={allSelected} onChange={onToggleAll} className="h-3.5 w-3.5 rounded border-line-strong" />
        {t('export.selectAllFields')}
      </label>
      <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
        {fields.map((field) => (
          <label key={field.key} className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={selected.has(field.key)}
              onChange={() => onToggle(field.key)}
              className="h-3.5 w-3.5 rounded border-line-strong"
            />
            {field.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function toggleSet(set: Set<string>, key: string): Set<string> {
  const next = new Set(set);
  next.has(key) ? next.delete(key) : next.add(key);
  return next;
}

export function ExportTicketsDrawer({ open, onClose, filters, ticketIds }: ExportTicketsDrawerProps) {
  const { t } = useI18n();
  const [catalog, setCatalog] = React.useState<FieldCatalog | null>(null);
  const [format, setFormat] = React.useState<'csv' | 'excel'>('csv');
  const [ticketFieldKeys, setTicketFieldKeys] = React.useState<Set<string>>(new Set(['ticketNumber', 'subject', 'status']));
  const [contactFieldKeys, setContactFieldKeys] = React.useState<Set<string>>(new Set());
  const [companyFieldKeys, setCompanyFieldKeys] = React.useState<Set<string>>(new Set());
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open || catalog) return;
    api.get<{ data: FieldCatalog }>('/tickets/export/fields')
      .then((response) => setCatalog(response.data))
      .catch(() => setError(t('export.loadError')));
  }, [open, catalog]);

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleExport() {
    setExporting(true);
    setError('');
    try {
      const blob = await api.downloadPost('/tickets/export', {
        format,
        filters: ticketIds ? undefined : Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, String(v)])),
        ticketIds,
        ticketFields: Array.from(ticketFieldKeys),
        contactFields: Array.from(contactFieldKeys),
        companyFields: Array.from(companyFieldKeys),
      });
      triggerDownload(blob, `tickets-export.${format === 'excel' ? 'xlsx' : 'csv'}`);
      onClose();
    } catch {
      setError(t('export.failed'));
    } finally {
      setExporting(false);
    }
  }

  async function handlePdfExport() {
    setExporting(true);
    setError('');
    try {
      if (ticketIds && ticketIds.length > 1) {
        const blob = await api.downloadPost('/tickets/export/pdf-zip', { ticketIds });
        triggerDownload(blob, 'tickets-pdf-export.zip');
      } else {
        const singleId = ticketIds?.[0];
        if (!singleId) return;
        const blob = await api.download(`/tickets/${singleId}/export/pdf`);
        triggerDownload(blob, `ticket-${singleId}.pdf`);
      }
      onClose();
    } catch {
      setError(t('export.pdfFailed'));
    } finally {
      setExporting(false);
    }
  }

  const totalSelected = ticketFieldKeys.size + contactFieldKeys.size + companyFieldKeys.size;
  const allFieldsSelected = !!catalog
    && catalog.ticketFields.every((f) => ticketFieldKeys.has(f.key))
    && catalog.contactFields.every((f) => contactFieldKeys.has(f.key))
    && catalog.companyFields.every((f) => companyFieldKeys.has(f.key));
  function toggleAllFields() {
    if (!catalog) return;
    if (allFieldsSelected) {
      setTicketFieldKeys(new Set());
      setContactFieldKeys(new Set());
      setCompanyFieldKeys(new Set());
    } else {
      setTicketFieldKeys(new Set(catalog.ticketFields.map((f) => f.key)));
      setContactFieldKeys(new Set(catalog.contactFields.map((f) => f.key)));
      setCompanyFieldKeys(new Set(catalog.companyFields.map((f) => f.key)));
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={<span className="flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" aria-hidden="true" />{t('export.title')}</span>}
      widthClassName="max-w-lg"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-ink-subtle">{t(totalSelected === 1 ? 'export.fieldsTotalOne' : 'export.fieldsTotalMany', { count: totalSelected })}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button onClick={() => void handleExport()} disabled={exporting || totalSelected === 0}>
              <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {exporting ? t('export.exporting') : t('tickets.export')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 p-5">
        {error && <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-600">{error}</p>}
        {ticketIds && (
          <div className="rounded-md border border-brand-200 bg-brand-50 px-3 py-3">
            <p className="text-sm text-brand-700">
              {ticketIds.length === 1 ? t('export.exportingOne') : t('export.exportingMany', { count: ticketIds.length })}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => void handlePdfExport()}
              disabled={exporting}
            >
              {ticketIds.length > 1 ? t('export.pdfZip', { count: ticketIds.length }) : t('export.pdf')}
            </Button>
            <p className="mt-1 text-xs text-brand-600">
              {ticketIds.length > 1
                ? t('export.pdfZipHint')
                : t('export.pdfHint')}
            </p>
          </div>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-ink-muted">{t('export.formatLabel')}</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="radio" name="export-format" checked={format === 'csv'} onChange={() => setFormat('csv')} />
              {t('export.csv')}
            </label>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              <input type="radio" name="export-format" checked={format === 'excel'} onChange={() => setFormat('excel')} />
              {t('export.excel')}
            </label>
          </div>
        </div>

        {!catalog ? (
          <p className="text-sm text-ink-subtle">{t('export.loadingFields')}</p>
        ) : (
          <>
            <label className="flex items-center gap-2 border-t pt-4 text-sm font-semibold text-brand-700">
              <input
                type="checkbox"
                checked={allFieldsSelected}
                onChange={toggleAllFields}
                className="h-3.5 w-3.5 rounded border-line-strong"
              />
              Select all fields (ticket, contact and company)
            </label>
            <FieldSection
              title={t('export.ticketFields')}
              fields={catalog.ticketFields}
              selected={ticketFieldKeys}
              onToggle={(key) => setTicketFieldKeys((prev) => toggleSet(prev, key))}
              onToggleAll={() => setTicketFieldKeys(
                ticketFieldKeys.size === catalog.ticketFields.length ? new Set() : new Set(catalog.ticketFields.map((f) => f.key)),
              )}
            />
            <FieldSection
              title={t('export.contactFields')}
              fields={catalog.contactFields}
              selected={contactFieldKeys}
              onToggle={(key) => setContactFieldKeys((prev) => toggleSet(prev, key))}
              onToggleAll={() => setContactFieldKeys(
                contactFieldKeys.size === catalog.contactFields.length ? new Set() : new Set(catalog.contactFields.map((f) => f.key)),
              )}
            />
            <FieldSection
              title={t('export.companyFields')}
              fields={catalog.companyFields}
              selected={companyFieldKeys}
              onToggle={(key) => setCompanyFieldKeys((prev) => toggleSet(prev, key))}
              onToggleAll={() => setCompanyFieldKeys(
                companyFieldKeys.size === catalog.companyFields.length ? new Set() : new Set(catalog.companyFields.map((f) => f.key)),
              )}
            />
          </>
        )}
      </div>
    </SlideOver>
  );
}
